'use client';

import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import { MarginDisplayInfo } from 'src/components/MarginInfo/MarginInfo';
import ShowHideBalanceButton from 'src/components/ShowHideAccountValuesButton/ShowHideAccountValuesButton';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { DefinitionTooltipID } from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import useShowAccountValues from '../../../hooks/useShowAccountValues';
import useWalletIsConnected from '../../../hooks/useWalletIsConnected';
import { Open, Info } from '@drift-labs/icons';
import { cj, cm } from 'src/utils/classNames';
import MarginCalcPanel from 'src/components/MarginInfo/MarginCalcPanel';
import ConnectToTradeButton from 'src/components/ConnectToTradeButton';
import useModal from 'src/components/Modals/hooks/useModal';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import { getColorVar } from 'src/utils/theme/colorVars';
import Button from 'src/components/primitives/Button/Button';
import { signDependentValue } from 'src/utils/signDependentValue';
import healthDependentClassName from 'src/utils/healthDependentClassName';
import useSettleAllPnls from 'src/hooks/drift-actions/useSettlePnls';

type MarginItemOption = {
	label: string;
	value: string | number;
	tooltipDefinitionId?: DefinitionTooltipID;
	textColorClass?: string;
};

type MarginItem = {
	label: string;
	value: string;
	options?: MarginItemOption[];
	tooltipDefinitionId?: DefinitionTooltipID;
	id?: string;
	showSettleButton?: boolean;
	actionIcon?: React.ReactNode;
};

const MemoizedLabel = React.memo(function MarginItemLabel(props: {
	label: string;
	tooltipDefinitionId?: DefinitionTooltipID;
}) {
	return (
		<MobileTableCard.Label>
			<DefinitionTooltip
				className="w-full"
				key={props.label}
				definitionId={props.tooltipDefinitionId}
			>
				{props.label}
			</DefinitionTooltip>
		</MobileTableCard.Label>
	);
});

const buildMarginItems = (
	showAccountValues: boolean,
	marginInfoDisplay: MarginDisplayInfo,
	separateHealthColumn?: boolean
) => {
	const maintenanceReqOption: MarginItemOption = {
		label: 'Maint. Margin',
		value: showAccountValues
			? `${marginInfoDisplay?.maintenanceReq ?? ''}${
					(marginInfoDisplay?.maintenanceReqPct &&
						` (${marginInfoDisplay?.maintenanceReqPct})`) ??
					''
				}`
			: '$ ∗∗∗∗∗',
		tooltipDefinitionId: 'tradingMaintenanceMargin',
	};

	const initialReqOption: MarginItemOption = {
		label: 'Initial Margin',
		value: showAccountValues
			? `${marginInfoDisplay?.initialReq ?? ''}${
					(marginInfoDisplay?.initialReqPct &&
						` (${marginInfoDisplay?.initialReqPct})`) ??
					''
				}`
			: '$ ∗∗∗∗∗',
		tooltipDefinitionId: 'tradingInitialMargin',
	};

	const healthColumnItems: MarginItem[] = separateHealthColumn
		? [
				{
					label: 'Account Health',
					value:
						marginInfoDisplay?.health != null
							? `${marginInfoDisplay?.health}%`
							: '',
					id: 'health',
					tooltipDefinitionId: 'tradingAccountHealth',
					options: [maintenanceReqOption, initialReqOption],
				},
			]
		: [];

	const items: MarginItem[] = [
		{
			label: 'Net USD Value',
			value: showAccountValues ? marginInfoDisplay?.netUsdValue : '$ ∗∗∗∗∗',
			actionIcon: <ShowHideBalanceButton className="mr-1" />,
			tooltipDefinitionId: 'tradingNetUsdValue',
			id: 'net_usd_value',
			showSettleButton: true,
			options: [
				{
					label: 'Balances',
					value: showAccountValues
						? marginInfoDisplay?.accountBalance
						: '$ ∗∗∗∗∗',
				},
				{
					label: 'Unsettled',
					value: showAccountValues
						? marginInfoDisplay?.unrealisedPnl
						: '$ ∗∗∗∗∗',
					textColorClass: !showAccountValues
						? 'text-text-default'
						: signDependentValue(marginInfoDisplay?.unsettledPnlNum, {
								positive: 'text-text-positive-green-button',
								negative: 'text-text-negative-red-button',
								zero: 'text-text-default',
							}),
					tooltipDefinitionId: 'unsettledBalance',
				},
			],
		},
		{
			label: 'Acct. Leverage',
			value: marginInfoDisplay?.accountLeverage,
			id: 'account_leverage',
			options: [
				{
					label: 'Total Collateral',
					value: showAccountValues
						? marginInfoDisplay?.totalCollateral
						: '$ ∗∗∗∗∗',
					tooltipDefinitionId: 'tradingTotalCollateral' as const,
				},
				{
					label: 'Free Collateral',
					value: showAccountValues
						? marginInfoDisplay?.freeCollateral
						: '$ ∗∗∗∗∗',
					tooltipDefinitionId: 'tradingFreeCollateral' as const,
				},
				{
					label: 'In Open Orders',
					value: showAccountValues
						? marginInfoDisplay?.totalQuoteInOrders
						: '$ ∗∗∗∗∗',
				},
				separateHealthColumn ? undefined : maintenanceReqOption,
			].filter((option) => !!option),
		},
		...healthColumnItems,
	];

	return items;
};

const AccountHealth = React.memo(function AccountHealthMemo(props: {
	marginInfoDisplay: MarginDisplayInfo;
}) {
	const [isSettling, setIsSettling] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [expandedItemIndex, setExpandedItemIndex] = useState(-1);
	const connected = useWalletIsConnected();

	const { settleAllPnls } = useSettleAllPnls();
	const { show } = useModal();

	const health = props.marginInfoDisplay?.health;

	const showAccountValues = useShowAccountValues();

	const handleShowHealthModal = useCallback(() => {
		show({ type: 'account_health', props: {} });
	}, [show]);

	const handleExpandItem = (index: number) => {
		if (expandedItemIndex !== index) {
			setExpandedItemIndex(index);
		} else {
			setExpandedItemIndex(-1);
		}
	};

	const handleSettle = async () => {
		setIsSettling(true);
		try {
			await settleAllPnls();
		} catch (err) {
			console.log(err);
		}
		setIsSettling(false);
	};

	const handleClickedOutsideContainer = useCallback(
		(event: MouseEvent) => {
			if (expandedItemIndex === -1) return;

			// If event comes from inside of wrapperRef, don't collapse
			let node = event.target as HTMLElement;
			while (node) {
				if (node === wrapperRef.current) {
					return;
				}
				node = node.parentElement as HTMLElement;
			}

			setExpandedItemIndex(-1);
		},
		[expandedItemIndex]
	);

	useEffect(() => {
		window.document.body.addEventListener(
			'mouseup',
			handleClickedOutsideContainer
		);
		return () =>
			window.document.body.removeEventListener(
				'mouseup',
				handleClickedOutsideContainer
			);
	}, [expandedItemIndex]);

	const marginItems = useMemo(() => {
		return buildMarginItems(showAccountValues, props.marginInfoDisplay);
	}, [showAccountValues, props.marginInfoDisplay]);

	return (
		<div className="w-full" ref={wrapperRef}>
			<div className="flex items-stretch justify-between divide-x divide-stroke-divider text-text-default">
				{marginItems.map((marginItem, i) => {
					return (
						<div key={marginItem.id} className="my-1 w-full px-1">
							<div
								className={cj(
									'flex h-full w-full flex-col gap-y-1 rounded-sm px-2 py-0.5 text-left select-none',
									connected && 'cursor-pointer'
								)}
								onClick={connected ? () => handleExpandItem(i) : undefined}
							>
								<MobileTableCard.Label>
									{marginItem.label}
								</MobileTableCard.Label>
								<span className="flex items-center gap-1 typo-t4">
									<span>{connected ? marginItem.value : '-'}</span>
									{connected && <Info color={getColorVar('text-secondary')} />}
								</span>
							</div>
						</div>
					);
				})}
				<div className="my-1 shrink-0 px-1">
					<div
						className={cj(
							'flex h-full flex-col gap-y-1 rounded-sm px-2 py-0.5 text-left whitespace-nowrap select-none',
							connected && 'cursor-pointer'
						)}
						onClick={connected ? handleShowHealthModal : undefined}
					>
						<MobileTableCard.Label>Health</MobileTableCard.Label>
						<span className="flex items-center gap-1">
							<span
								className={cj(
									'typo-t4',
									healthDependentClassName(health, { type: 'text' })
								)}
							>
								{connected ? `${health}%` : '-'}
							</span>
							{connected && <Open color={getColorVar('text-secondary')} />}
						</span>
					</div>
				</div>
			</div>
			<div
				className={cj(
					'space-y-2 p-4',
					expandedItemIndex > -1 ? 'block' : 'hidden'
				)}
			>
				{marginItems[expandedItemIndex]?.options?.map((option) => {
					return (
						<div
							className="flex items-center justify-between"
							key={option.label}
						>
							<MemoizedLabel
								tooltipDefinitionId={option.tooltipDefinitionId}
								label={option.label}
							/>
							<span
								className={cm(
									'typo-t4 text-text-default',
									option.textColorClass
								)}
							>
								{option.value}
							</span>
						</div>
					);
				})}
				{marginItems[expandedItemIndex]?.showSettleButton && (
					<Button
						size="md"
						variant="secondary"
						className="mt-2 w-full"
						onClick={handleSettle}
						disabled={
							isSettling ||
							!connected ||
							props.marginInfoDisplay?.unsettledPnlNum === 0
						}
						isLoading={isSettling}
					>
						Settle
					</Button>
				)}
			</div>
		</div>
	);
});

const MobileAndTabletAccountHealth = React.memo(
	function MobileAndTabletAccountHealth() {
		const connected = useWalletIsConnected();

		const userClient = useDriftAccountStore(
			(s) => s.accounts[s.currentUserKey ?? '']?.client
		);
		const isUserAccountReady = useUserAccountIsReady();

		// Get marginCalc to check for isolated positions
		const marginCalc = useMemo(() => {
			return userClient && isUserAccountReady
				? userClient.getMarginCalculation('Maintenance')
				: undefined;
		}, [userClient, isUserAccountReady]);

		// Check if isolated positions exist
		const hasIsolatedPositions = useMemo(() => {
			if (!marginCalc) return false;
			return marginCalc.isolatedMarginCalculations.size > 0;
		}, [marginCalc]);

		return (
			<div className="h-full">
				{connected ? (
					<div
						className={cj(
							MOBILE_TABLE_CARD_LIST_CLASSNAME,
							'flex h-full flex-col items-stretch justify-between text-text-default',
							'sm:py-2'
						)}
					>
						<MarginCalcPanel
							filteredMarginCalcs="cross"
							className="w-full sm:pt-0"
							includeDetailsAndDepositButtons={true}
						/>

						{hasIsolatedPositions && (
							<MarginCalcPanel
								filteredMarginCalcs="isolated"
								className="w-full sm:pt-0"
							/>
						)}
					</div>
				) : (
					<div className="flex h-full items-center justify-center">
						<ConnectToTradeButton />
					</div>
				)}
			</div>
		);
	}
);

export { MobileAndTabletAccountHealth, AccountHealth };
